import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {useMutation} from '@github-ui/react-query'
import type {
  MilestoneState,
  UpdateMilestoneMutationMutation,
} from '@github-ui/server-persisted-queries/types/updateMilestoneMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {LABELS} from './constants/labels'

export type UpdateMilestoneStateVariables = {
  id: string
  number: number
  nameWithOwner: string
  state: MilestoneState
}

async function updateMilestoneState({
  number,
  nameWithOwner,
  state,
}: UpdateMilestoneStateVariables): Promise<UpdateMilestoneMutationMutation> {
  let response: Response
  try {
    // The milestone is identified by `number` in the path; the companion github/github
    // MilestonesController action derives the node id server-side, so it is not sent in the body.
    response = await reactFetchJSON(`/${nameWithOwner}/milestones/${number}/state`, {
      method: 'PUT',
      body: {state},
    })
  } catch {
    throw new Error(LABELS.milestoneEditError)
  }

  // Parse the body once (best-effort). A non-2xx status or a GraphQL `errors` payload both
  // surface the generic edit error, but we thread any server-provided error messages through
  // `cause` so MilestoneEdit's `error.cause` branch can render them via formatFormErrorMessage.
  let result: GraphQLResponse<UpdateMilestoneMutationMutation> | undefined
  try {
    result = (await response.json()) as GraphQLResponse<UpdateMilestoneMutationMutation>
  } catch {
    result = undefined
  }

  const errors = result && 'errors' in result ? result.errors : undefined

  if (!response.ok || (errors && errors.length > 0)) {
    throw errors && errors.length > 0
      ? new Error(LABELS.milestoneEditError, {cause: errors})
      : new Error(LABELS.milestoneEditError)
  }

  if (!result || !('data' in result) || !result.data || !result.data.updateMilestone?.milestone) {
    throw new Error(LABELS.milestoneEditError)
  }

  return result.data
}

/**
 * TanStack Query mutation that toggles a milestone's open/closed state.
 *
 * Optimistic parity is preserved by emitting `mutation-lifecycle-events`
 * (optimistic/completed/error, correlated by a single `mutationId`):
 * any TSQ cache that subscribes via `useSyncObjectPropertiesPatchToQueryCache` gets the optimistic
 * `closed` flip and rolls it back on error.
 */
export function useUpdateMilestoneState() {
  return useMutation<UpdateMilestoneMutationMutation, Error, UpdateMilestoneStateVariables, {mutationId: string}>({
    mutationFn: updateMilestoneState,
    onMutate: ({id, state}) => {
      const patch: ObjectPropertiesPatch = {id, values: {closed: state === 'CLOSED'}}
      const event = new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
        phase: 'optimistic',
        data: patch,
      })
      dispatchMutationLifecycleEvent(event)
      return {mutationId: event.detail.mutationId}
    },
    onError: (error, _variables, context) => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
          phase: 'error',
          mutationId: context?.mutationId ?? createMutationId(),
          error,
        }),
      )
    },
    onSuccess: (data, _variables, context) => {
      const milestone = data.updateMilestone?.milestone
      if (!milestone) return
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
          phase: 'completed',
          mutationId: context?.mutationId ?? createMutationId(),
          data: {id: milestone.id, values: {closed: milestone.closed}},
        }),
      )
    },
  })
}
